import {
  CreateJobConfigRequest,
  JOB_CONFIGS_PATH,
  JobConfig,
  jobConfigSchema,
  ListJobConfigsResponse,
  listJobConfigsResponseSchema,
  UpdateJobConfigRequest,
} from '@quizdb/common';
import { quizdbApi } from '../api/quizdbApi';

/** The job configurations collection path with one id appended (QZ-D194), as `sourcePath` is. */
const jobConfigPath = (jobConfigId: string): string =>
  `${JOB_CONFIGS_PATH}/${encodeURIComponent(jobConfigId)}`;

/** The job configuration admin screens' endpoints (QZ-D194, EXJ-D3). */
export const jobConfigApi = quizdbApi.injectEndpoints({
  endpoints: (builder) => ({
    listJobConfigs: builder.query<ListJobConfigsResponse, void>({
      query: () => JOB_CONFIGS_PATH,
      transformResponse: (response: unknown) => listJobConfigsResponseSchema.parse(response),
      providesTags: ['JobConfigList'],
    }),
    getJobConfig: builder.query<JobConfig, string>({
      query: (jobConfigId) => jobConfigPath(jobConfigId),
      transformResponse: (response: unknown) => jobConfigSchema.parse(response),
      providesTags: (_result, _error, jobConfigId) => [{ type: 'JobConfig', id: jobConfigId }],
    }),
    createJobConfig: builder.mutation<JobConfig, CreateJobConfigRequest>({
      query: (request) => ({ url: JOB_CONFIGS_PATH, method: 'POST', body: request }),
      transformResponse: (response: unknown) => jobConfigSchema.parse(response),
      invalidatesTags: ['JobConfigList'],
    }),
    updateJobConfig: builder.mutation<JobConfig, { id: string; body: UpdateJobConfigRequest }>({
      query: ({ id, body }) => ({
        url: jobConfigPath(id),
        method: 'PUT',
        body,
      }),
      transformResponse: (response: unknown) => jobConfigSchema.parse(response),
      invalidatesTags: (_result, _error, { id }) => ['JobConfigList', { type: 'JobConfig', id }],
    }),
    deleteJobConfig: builder.mutation<void, string>({
      // The API answers 204, so there is nothing to parse.
      query: (jobConfigId) => ({ url: jobConfigPath(jobConfigId), method: 'DELETE' }),
      invalidatesTags: (_result, _error, jobConfigId) => [
        'JobConfigList',
        { type: 'JobConfig', id: jobConfigId },
      ],
    }),
  }),
});

export const {
  useListJobConfigsQuery,
  useGetJobConfigQuery,
  useCreateJobConfigMutation,
  useUpdateJobConfigMutation,
  useDeleteJobConfigMutation,
} = jobConfigApi;
